import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { InboundItem, LocationItem } from "../../types/item"; 

export interface DropdownOption {
  value: string;
  label: string;
}

// Raw API response types
export interface PackageItem {
  id: number;
  value: string;
}

interface InboundItemFormState {
  formData: InboundItem | {};
  isEditing: boolean;
  lastSaved: number | null;
  packages: PackageItem[];
  locations: LocationItem[];
}

const initialState: InboundItemFormState = {
  formData: {},
  isEditing: false,
  lastSaved: null,
  packages: [],
  locations: [],
};

const inboundItemFormSlice = createSlice({
  name: "inboundItemForm",
  initialState,
  reducers: {
    setFormData: (state, action: PayloadAction<InboundItem>) => {
      state.formData = action.payload;
      state.isEditing = true;
    },
    updateFormField: <Key extends keyof InboundItem>(
      state: InboundItemFormState,
      action: PayloadAction<{ field: Key; value: InboundItem[Key] }>
    ) => {
      // console.log(action.payload);
      // state.formData = { ...state.formData, [action.payload.field]: action.payload.value };
      if (state.formData && typeof state.formData === 'object') {
        (state.formData as any)[action.payload.field] = action.payload.value;
      }
    },
    clearFormData: (state) => {
      state.formData = {};
      state.isEditing = false;
      state.lastSaved = null;
      state.packages = [];
      state.locations = [];
    },
    setLastSaved: (state, action: PayloadAction<number>) => {
      state.lastSaved = action.payload;
    },
    setPackages: (state, action: PayloadAction<PackageItem[]>) => {
      state.packages = action.payload;
    },
    setLocations: (state, action: PayloadAction<LocationItem[]>) => {
      state.locations = action.payload;
    },
  },
});

export const { 
  setFormData, 
  updateFormField, 
  clearFormData, 
  setLastSaved,
  setPackages,
  setLocations,
} = inboundItemFormSlice.actions;

// Selector functions for better usage
export const getFormData = (state: { inboundItemForm: InboundItemFormState }) =>
  state?.inboundItemForm?.formData as InboundItem;

export const selectIsEditing = (state: {
  inboundItemForm: InboundItemFormState;
}) => state.inboundItemForm.isEditing;

export const selectLastSaved = (state: {
  inboundItemForm: InboundItemFormState;
}) => state.inboundItemForm.lastSaved;

// Transform raw API data to dropdown format with memoization
export const selectPackageOptions = createSelector(
  [(state: { inboundItemForm: InboundItemFormState }) => state.inboundItemForm.packages],
  (packages): DropdownOption[] => {
    const packageList = packages || [];
    return packageList.map((item) => ({
      value: item.id.toString(),
      label: item.value,
    }));
  }
);

export const selectLocationOptions = createSelector(
  [(state: { inboundItemForm: InboundItemFormState }) => state.inboundItemForm.locations],
  (locations): DropdownOption[] => {
    const locationList = locations || [];
    return locationList.map((item) => ({
      value: item.id.toString(),
      label: item.value,
    }));
  }
);



export default inboundItemFormSlice.reducer;
